// src/components/eframes/EFrame145.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from "react-router-dom";
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import { PeopleIcon } from '../ui/Icons';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface MemoryCollection {
  personId: string;
  memories: string;
  files: AppDocument[];
}

interface MemoryCollectionWithPerson {
  person: UnifiedContact;
  collection: MemoryCollection;
}

/**
 * Executor read-only version of Frame145.
 *
 * Security/read-only guarantees:
 * - No create/edit/delete memory actions
 * - No upload flow
 * - No document remove/delete flow
 * - No modal editor
 * - No task progress mutation
 * - Only reads session data and displays attached document names
 */
const EFrame145: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const estateOwnerName = getPlanOwnerName(session.name);

  const [people, setPeople] = useState<UnifiedContact[]>([]);
  const [memoryCollections, setMemoryCollections] = useState<MemoryCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        const contacts = estateId
          ? await unifiedContactService.getContactsForEstate(estateId)
          : await unifiedContactService.getAllContacts();
        setPeople(contacts);

        const localTask = session.taskProgress?.memoryCollections;
        const answers = localTask?.answers as { memoryCollections?: MemoryCollection[] } | undefined;

        if (answers?.memoryCollections) {
          const collections = answers.memoryCollections.map((collection) => ({
            ...collection,
            files: documentService.normalizeMany(collection.files || []),
          }));

          setMemoryCollections(collections);
        } else {
          setMemoryCollections([]);
        }
      } catch (error) {
        console.error('[EFrame145] Error loading data:', error);
        setPeople([]);
        setMemoryCollections([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [estateId, session.taskProgress?.memoryCollections]);

  const memoryCollectionsWithPeople: MemoryCollectionWithPerson[] = useMemo(() => {
    return memoryCollections
      .map((collection) => {
        const person = people.find((p) => p.id === collection.personId);
        if (!person) return null;

        return { person, collection };
      })
      .filter((item): item is MemoryCollectionWithPerson => item !== null);
  }, [people, memoryCollections]);

  const getFileTypeIcon = (mimeType?: string) => {
    const fileType = mimeType || '';

    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('doc')) return '📝';
    if (fileType.includes('txt')) return '📄';

    return '📁';
  };

  const toggleExpandedCard = (personId: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [personId]: !prev[personId],
    }));
  };

  const totalMemories = memoryCollectionsWithPeople.length;

  const statCards = [
    { label: 'Total Memories', value: totalMemories },
  ];

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px]">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <ResponsiveHeader title={`${estateOwnerName}’s Memory Collections`} />
            </div>
          </div>

          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            View the messages, notes, and memories created for {estateOwnerName}.
          </p>

          <p className="mt-3 text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            These collections help preserve their legacy and provide meaningful context for family and loved ones.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-md border border-stone-200 p-6"
              >
                <div className="flex items-start gap-3">
                  <PeopleIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />
                  <div className="flex flex-col">
                    <div className="font-sans text-base text-stone-600">
                      {card.label}
                    </div>
                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4">
                      {loading ? '—' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7">
            <h2 className="font-sans text-xl font-medium text-stone-800 mb-3">
              Memory Collections
            </h2>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading memory collections...</p>
                </div>
              ) : memoryCollectionsWithPeople.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">
                    No memory collections available.
                  </p>
                </div>
              ) : (
                memoryCollectionsWithPeople.map(({ person, collection }) => {
                  const isExpanded = !!expandedCards[person.id];
                  const hasNotes = collection.memories?.trim().length > 0;
                  const hasFiles = (collection.files?.length || 0) > 0;

                  return (
                    <div
                      key={person.id}
                      className="bg-white rounded-lg border border-stone-200 p-6"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <PeopleIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                        <div className="flex-1 min-w-0">
                          <div className="font-sans text-lg font-medium text-stone-800">
                            {person.firstName} {person.lastName}
                          </div>

                          <div className="font-sans text-sm text-stone-500 mt-2">
                            {hasNotes && hasFiles
                              ? 'Notes • Files'
                              : hasNotes
                                ? 'Notes'
                                : hasFiles
                                  ? 'Files'
                                  : 'No content'}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                          <button
                            type="button"
                            onClick={() => toggleExpandedCard(person.id)}
                            className="text-stone-600 hover:text-stone-800 px-3 py-1 rounded border border-stone-300 hover:border-stone-500 font-sans text-sm font-medium whitespace-nowrap transition-colors"
                          >
                            {isExpanded ? 'Collapse' : 'View Note'}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-stone-200 space-y-4">
                          {hasNotes && (
                            <div>
                              <div className="font-sans text-sm font-medium text-stone-700 mb-2">
                                Notes
                              </div>
                              <div className="font-sans text-sm text-stone-600 whitespace-pre-wrap leading-relaxed">
                                {collection.memories}
                              </div>
                            </div>
                          )}

                          {hasFiles && (
                            <div>
                              <div className="font-sans text-sm font-medium text-stone-700 mb-2">
                                Files
                              </div>

                              <div className="space-y-2">
                                {collection.files.map((file) => (
                                  <div
                                    key={file.id}
                                    className="flex items-start gap-3 p-3 rounded-md border border-stone-200"
                                  >
                                    <span className="text-xl flex-shrink-0">
                                      {getFileTypeIcon(file.mimeType)}
                                    </span>

                                    <div className="min-w-0">
                                      <div className="font-sans text-sm font-medium text-stone-800 break-words whitespace-normal">
                                        {file.name}
                                      </div>

                                      {typeof file.size === "number" && (
                                        <div className="font-sans text-xs text-stone-500">
                                          {(file.size / 1024 / 1024).toFixed(2)} MB
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {!hasNotes && !hasFiles && (
                            <div className="font-sans text-sm text-stone-500">
                              No memory content available.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame145;